import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Search,
  MapPin,
  Calendar,
  Phone,
  Mail,
  Eye,
  AlertTriangle,
  Filter,
  Plus,
  Heart,
  Clock,
  Share2,
  Map,
  ExternalLink,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const LostDogsPage = ({ user }) => {
  const { themeClasses } = useTheme();
  const [lostDogs, setLostDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedDog, setSelectedDog] = useState(null);
  const [showSightingModal, setShowSightingModal] = useState(false);
  const [sightingData, setSightingData] = useState({
    location: "",
    description: "",
    contactInfo: "",
    sightingDate: new Date().toISOString().split("T")[0],
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedDogForStatus, setSelectedDogForStatus] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    notes: "",
  });

  useEffect(() => {
    // Use Firebase Function to get lost dogs
    const fetchLostDogs = async () => {
      try {
        const getNearbyLostDogs = httpsCallable(functions, "getNearbyLostDogs");
        const result = await getNearbyLostDogs({
          location: "all", // Get all missing dogs
          limit: 100,
        });

        if (result.data.success) {
          setLostDogs(result.data.lostDogs);
        }
      } catch (error) {
        console.error("Error fetching lost dogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLostDogs();
  }, []);

  const filteredDogs = lostDogs.filter((dog) => {
    const matchesSearch =
      dog.petName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dog.breed?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dog.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation =
      !locationFilter ||
      dog.lastSeenLocation
        ?.toLowerCase()
        .includes(locationFilter.toLowerCase());

    const matchesDate =
      dateFilter === "all" ||
      (() => {
        const dogDate = new Date(dog.dateLost);
        const now = new Date();
        const daysDiff = Math.floor((now - dogDate) / (1000 * 60 * 60 * 24));

        switch (dateFilter) {
          case "today":
            return daysDiff === 0;
          case "week":
            return daysDiff <= 7;
          case "month":
            return daysDiff <= 30;
          default:
            return true;
        }
      })();

    return matchesSearch && matchesLocation && matchesDate;
  });

  const handleSightingReport = async (dogId) => {
    try {
      const reportSighting = httpsCallable(functions, "reportSighting");
      const result = await reportSighting({
        dogId,
        location: sightingData.location,
        description: sightingData.description,
        sightingDate: sightingData.sightingDate,
        contactInfo: user?.email || sightingData.contactInfo,
        reporterName: user?.displayName || "Anonymous",
      });

      if (result.data.success) {
        // Reset form and close modal
        setSightingData({
          location: "",
          description: "",
          contactInfo: "",
          sightingDate: new Date().toISOString().split("T")[0],
        });
        setShowSightingModal(false);
        setSelectedDog(null);

        alert("Sighting reported successfully! The owner will be notified.");
      }
    } catch (error) {
      console.error("Error reporting sighting:", error);
      alert("Error reporting sighting. Please try again.");
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const updateLostDogStatus = httpsCallable(
        functions,
        "updateLostDogStatus"
      );
      const result = await updateLostDogStatus({
        dogId: selectedDogForStatus.id,
        status: statusUpdate.status,
        notes: statusUpdate.notes,
      });

      if (result.data.success) {
        // Reset form and close modal
        setStatusUpdate({ status: "", notes: "" });
        setShowStatusModal(false);
        setSelectedDogForStatus(null);

        // Refresh the lost dogs list
        window.location.reload();

        const statusMessage = {
          found: "Great news! Your dog has been marked as found! 🎉",
          cancelled: "Your lost dog report has been cancelled.",
          deceased: "We're sorry for your loss. The report has been updated.",
        };

        alert(
          statusMessage[statusUpdate.status] || "Status updated successfully!"
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) return "Today";
    if (daysDiff === 1) return "Yesterday";
    if (daysDiff < 7) return `${daysDiff} days ago`;
    if (daysDiff < 30) return `${Math.floor(daysDiff / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const shareOnSocial = (dog) => {
    const text = `LOST DOG: ${dog.petName} - ${dog.breed}. Last seen: ${
      dog.lastSeenLocation
    } on ${formatDate(dog.dateLost)}. Please help us find ${
      dog.petName
    }! Contact: ${dog.contactPhone}`;
    const url = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: `Help Find ${dog.petName}`,
        text: text,
        url: url,
      });
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(`${text}\n${url}`);
      alert("Information copied to clipboard!");
    }
  };

  const openGoogleMaps = (dog) => {
    // Use the lastSeenAddress if available, otherwise use lastSeenLocation
    const location = dog.lastSeenAddress || dog.lastSeenLocation;

    if (!location || location.trim() === "") {
      alert("No location information available for this dog.");
      return;
    }

    try {
      // Add context to the search if we have pet name
      const searchQuery = dog.petName
        ? `${location} (Lost dog: ${dog.petName})`
        : location;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        searchQuery
      )}`;

      // Open in new tab
      window.open(googleMapsUrl, "_blank");

      console.log(
        `Opening Google Maps for ${dog.petName} at location: ${location}`
      );
    } catch (error) {
      console.error("Error opening Google Maps:", error);
      alert("Unable to open Google Maps. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1
              className={`${themeClasses.text.primary} text-3xl font-bold mb-2`}
            >
              Lost Dogs Registry
            </h1>
            <p className={`${themeClasses.text.secondary} text-lg`}>
              Help reunite lost dogs with their families
            </p>
          </div>
          <div className="text-right">
            <div className={`${themeClasses.text.primary} text-2xl font-bold`}>
              {filteredDogs.length}
            </div>
            <div className={`${themeClasses.text.secondary} text-sm`}>
              Missing Dogs
            </div>
          </div>
        </div>

        {/* Alert Banner */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200 font-medium">
              Every minute counts! If you see a lost dog, please report it
              immediately.
            </span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.text.secondary} w-4 h-4`}
            />
            <input
              type="text"
              placeholder="Search by name, breed, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>

          <div className="relative">
            <MapPin
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.text.secondary} w-4 h-4`}
            />
            <input
              type="text"
              placeholder="Filter by location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>

          <div className="relative">
            <Calendar
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.text.secondary} w-4 h-4`}
            />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <button
            onClick={() =>
              (window.location.href = "/dashboard/register-lost-dog")
            }
            className={`${themeClasses.bg.button} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center space-x-2`}
          >
            <Plus className="w-4 h-4" />
            <span>Report Lost Dog</span>
          </button>
        </div>
      </div>

      {/* Lost Dogs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDogs.map((dog) => (
          <div
            key={dog.id}
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 hover:shadow-lg transition-shadow`}
          >
            {/* Dog Image */}
            <div className="aspect-square rounded-lg overflow-hidden mb-4 bg-gray-200 dark:bg-gray-700">
              {dog.photoURL ? (
                <img
                  src={dog.photoURL}
                  alt={dog.petName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className={`${themeClasses.text.secondary} text-center`}>
                    <div className="text-4xl mb-2">🐕</div>
                    <div className="text-sm">No Photo</div>
                  </div>
                </div>
              )}
            </div>

            {/* Dog Info */}
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3
                    className={`${themeClasses.text.primary} text-xl font-bold`}
                  >
                    {dog.petName}
                  </h3>
                  <p className={`${themeClasses.text.secondary} text-sm`}>
                    {dog.breed} • {dog.gender} • {dog.age}
                  </p>
                </div>
                <div className="flex items-center space-x-1 text-red-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {formatDate(dog.dateLost)}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-2 flex-1">
                    <MapPin
                      className={`w-4 h-4 ${themeClasses.text.secondary} mt-0.5 flex-shrink-0`}
                    />
                    <span className={`${themeClasses.text.secondary} text-sm`}>
                      Last seen: {dog.lastSeenLocation}
                    </span>
                  </div>
                  <button
                    onClick={() => openGoogleMaps(dog)}
                    className={`ml-2 p-1 rounded hover:${themeClasses.bg.hover} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors flex items-center space-x-1`}
                    title="View on Google Maps"
                  >
                    <Map className="w-4 h-4" />
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>

                {dog.reward && (
                  <div className="flex items-center space-x-2">
                    <Heart className="w-4 h-4 text-green-600" />
                    <span className="text-green-600 text-sm font-medium">
                      Reward: {dog.reward}
                    </span>
                  </div>
                )}

                {dog.description && (
                  <p
                    className={`${themeClasses.text.secondary} text-sm line-clamp-2`}
                  >
                    {dog.description}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 pt-2">
                {/* Owner-only buttons */}
                {user && dog.ownerId === user.uid ? (
                  <>
                    <button
                      onClick={() => {
                        setSelectedDogForStatus(dog);
                        setStatusUpdate({ status: "found", notes: "" });
                        setShowStatusModal(true);
                      }}
                      className="flex-1 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-1"
                    >
                      <span>✅ Mark as Found</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDogForStatus(dog);
                        setStatusUpdate({ status: "cancelled", notes: "" });
                        setShowStatusModal(true);
                      }}
                      className={`px-3 py-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors text-sm`}
                    >
                      Cancel Report
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setSelectedDog(dog);
                        setShowSightingModal(true);
                      }}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Report Sighting</span>
                    </button>

                    <button
                      onClick={() => openGoogleMaps(dog)}
                      className={`px-3 py-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors flex items-center space-x-1`}
                      title="View location on Google Maps"
                    >
                      <Map className="w-4 h-4" />
                      <span className="hidden sm:inline">Map</span>
                    </button>

                    <button
                      onClick={() => shareOnSocial(dog)}
                      className={`px-3 py-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
                    >
                      <Share2 className="w-4 h-4" />
                    </button>

                    <a
                      href={`tel:${dog.contactPhone}`}
                      className={`px-3 py-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
                    >
                      <Phone className="w-4 h-4" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredDogs.length === 0 && (
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-12 text-center`}
        >
          <div className="text-6xl mb-4">🐕</div>
          <h3 className={`${themeClasses.text.primary} text-xl font-bold mb-2`}>
            No Lost Dogs Found
          </h3>
          <p className={`${themeClasses.text.secondary} mb-4`}>
            {searchTerm || locationFilter || dateFilter !== "all"
              ? "Try adjusting your search filters"
              : "Great news! No dogs are currently reported missing in this area."}
          </p>
        </div>
      )}

      {/* Sighting Report Modal */}
      {showSightingModal && selectedDog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`${themeClasses.bg.card} rounded-lg p-6 w-full max-w-md mx-4`}
          >
            <h3
              className={`${themeClasses.text.primary} text-xl font-bold mb-4`}
            >
              Report Sighting: {selectedDog.petName}
            </h3>

            <div className="space-y-4">
              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Where did you see {selectedDog.petName}? *
                </label>
                <input
                  type="text"
                  placeholder="Exact location or address"
                  value={sightingData.location}
                  onChange={(e) =>
                    setSightingData({
                      ...sightingData,
                      location: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                  required
                />
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  When did you see {selectedDog.petName}?
                </label>
                <input
                  type="date"
                  value={sightingData.sightingDate}
                  onChange={(e) =>
                    setSightingData({
                      ...sightingData,
                      sightingDate: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                />
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Description
                </label>
                <textarea
                  placeholder="Describe the dog's condition, behavior, or any other details..."
                  value={sightingData.description}
                  onChange={(e) =>
                    setSightingData({
                      ...sightingData,
                      description: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                  rows="3"
                />
              </div>

              {!user && (
                <div>
                  <label
                    className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                  >
                    Your Contact Info *
                  </label>
                  <input
                    type="text"
                    placeholder="Phone or email"
                    value={sightingData.contactInfo}
                    onChange={(e) =>
                      setSightingData({
                        ...sightingData,
                        contactInfo: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                    required
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => handleSightingReport(selectedDog.id)}
                disabled={
                  !sightingData.location || (!user && !sightingData.contactInfo)
                }
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Report Sighting
              </button>
              <button
                onClick={() => {
                  setShowSightingModal(false);
                  setSelectedDog(null);
                  setSightingData({
                    location: "",
                    description: "",
                    contactInfo: "",
                    sightingDate: new Date().toISOString().split("T")[0],
                  });
                }}
                className={`px-4 py-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedDogForStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`${themeClasses.bg.card} rounded-lg p-6 w-full max-w-md mx-4`}
          >
            <h3
              className={`${themeClasses.text.primary} text-xl font-bold mb-4`}
            >
              Update Status: {selectedDogForStatus.petName}
            </h3>

            <div className="space-y-4">
              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-2`}
                >
                  Status
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      value="found"
                      checked={statusUpdate.status === "found"}
                      onChange={(e) =>
                        setStatusUpdate({
                          ...statusUpdate,
                          status: e.target.value,
                        })
                      }
                      className="text-green-600"
                    />
                    <span className={themeClasses.text.primary}>
                      ✅ Found - Dog has been safely returned!
                    </span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      value="cancelled"
                      checked={statusUpdate.status === "cancelled"}
                      onChange={(e) =>
                        setStatusUpdate({
                          ...statusUpdate,
                          status: e.target.value,
                        })
                      }
                      className="text-gray-600"
                    />
                    <span className={themeClasses.text.primary}>
                      ❌ Cancel Report - False alarm or no longer needed
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Additional Notes (Optional)
                </label>
                <textarea
                  placeholder="Any additional details about the resolution..."
                  value={statusUpdate.notes}
                  onChange={(e) =>
                    setStatusUpdate({
                      ...statusUpdate,
                      notes: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                  rows="3"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleStatusUpdate}
                disabled={!statusUpdate.status}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Status
              </button>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedDogForStatus(null);
                  setStatusUpdate({ status: "", notes: "" });
                }}
                className={`px-4 py-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LostDogsPage;
